import React, { FC, HTMLAttributes, ReactNode } from 'react';
import { styled } from 'linaria/react';
import { withTheme } from '@shared/lib/theme';
import { Text } from '@shared/ui/atoms';
import { chioseChipBorderRadiusesPx, chioseChipBorderRadiusesRem, chioseChipPaddingsPx, chioseChipPaddingsRem, choiseChipSizes, choiseChipTextSizes } from '@shared/config/components/choiseChip';
type SizeValue = keyof typeof choiseChipSizes;
type ResponsiveSize = SizeValue | {
  mobile: SizeValue;
  desktop: SizeValue;
};
interface ChoiseChipProps extends HTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'selected' | 'selectedAlternative';
  size?: ResponsiveSize;
  disabled?: boolean;
  icon?: ReactNode;
  tooltip?: ReactNode;
}
const ChoiseChip: FC<ChoiseChipProps> = ({
  variant = 'default',
  size = 'l',
  disabled = false,
  icon,
  tooltip,
  children,
  ...rest
}) => {
  const desktopSize = typeof size === 'object' ? size.desktop || 'l' : size || 'l';
  const mobileSize = typeof size === 'object' ? size.mobile || desktopSize : size || 'l';
  return <ChoiseChipWrapper type="button" variant={variant} desktopSize={desktopSize} mobileSize={mobileSize} disabled={disabled} {...rest}>
      {icon && <IconWrapper variant={variant}>{icon}</IconWrapper>}
      <Text family="MTSCompact" weight="medium" size={{
      desktop: choiseChipTextSizes[desktopSize],
      mobile: choiseChipTextSizes[mobileSize]
    }} color={variant === 'selected' ? 'inverted' : 'secondary'}>
        {children}
      </Text>
      {tooltip && <TooltipWrapper onClick={e => e.stopPropagation()} variant={variant}>
          {tooltip}
        </TooltipWrapper>}
    </ChoiseChipWrapper>;
};
export default ChoiseChip;
type ChoiseChipWrapperProps = Required<Pick<ChoiseChipProps, 'variant' | 'disabled'>> & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp = /*#__PURE__*/() => ({
  desktopSize
}) => chioseChipPaddingsRem[desktopSize];
const _exp2 = /*#__PURE__*/() => ({
  variant,
  theme
}) => variant === 'selected' ? theme.colors.background.inverted : theme.colors.background.tertiary;
const _exp3 = /*#__PURE__*/() => ({
  variant,
  theme
}) => variant === 'selectedAlternative' ? theme.colors.stroke.active : 'transparent';
const _exp4 = /*#__PURE__*/() => ({
  desktopSize
}) => chioseChipBorderRadiusesRem[desktopSize];
const _exp5 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? '0.5' : '1';
const _exp6 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? 'none' : 'auto';
const _exp8 = /*#__PURE__*/() => ({
  theme
}) => `inset 0 0 0 9999px ${theme.colors.background.hover}`;
const _exp9 = /*#__PURE__*/() => ({
  mobileSize
}) => chioseChipPaddingsPx[mobileSize];
const _exp0 = /*#__PURE__*/() => ({
  mobileSize
}) => chioseChipBorderRadiusesPx[mobileSize];
const ChoiseChipWrapper = withTheme(/*#__PURE__*/styled('button')({
  name: "ChoiseChipWrapper",
  class: "c1o917fi",
  propsAsIs: false,
  vars: {
    "c1o917fi-0": [_exp()],
    "c1o917fi-1": [_exp2()],
    "c1o917fi-2": [_exp3()],
    "c1o917fi-3": [_exp4()],
    "c1o917fi-4": [_exp5()],
    "c1o917fi-5": [_exp6()],
    "c1o917fi-6": [_exp8()],
    "c1o917fi-7": [_exp9()],
    "c1o917fi-8": [_exp0()]
  }
}));
type IconWrapperProps = Required<Pick<ChoiseChipProps, 'variant'>>;
const _exp1 = /*#__PURE__*/() => ({
  variant,
  theme
}) => variant === 'default' ? theme.colors.icon.secondary : theme.colors.icon.inverted;
const IconWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "il7rmsj",
  propsAsIs: false,
  vars: {
    "il7rmsj-0": [_exp1()]
  }
}));
type TooltipWrapperProps = Required<Pick<ChoiseChipProps, 'variant'>>;
const _exp10 = /*#__PURE__*/() => ({
  variant,
  theme
}) => variant === 'default' ? theme.colors.icon.secondary : theme.colors.icon.inverted;
const TooltipWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "TooltipWrapper",
  class: "t1os711",
  propsAsIs: false,
  vars: {
    "t1os711-0": [_exp10()]
  }
}));

require("./ChoiseChip.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./ChoiseChip.tsx");